import React from 'react';
import PropTypes from 'prop-types';
import { Map, IconCollection } from '../../../../components';
import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage } from '../../../../util/reactIntl';
import {
  EXACT_LOCATION_ZOOM,
  buildLocationMapsConfig,
  shouldShowExactLocation,
  useMapboxReady,
} from '../../../../components/Map/locationMapPresentation';
import css from './StoreLocationMap.module.css';

const StoreLocationMap = ({ geolocation, address, showAddressInStore = false }) => {
  const config = useConfiguration();
  const isMapboxReady = useMapboxReady();

  if (!geolocation || !geolocation.lat || !geolocation.lng) {
    return null;
  }

  // The store owner can publish their exact address from Store Settings. Falls
  // back to the privacy circle whenever the address itself is missing.
  const showExactLocation = shouldShowExactLocation(showAddressInStore, address);
  const mapMapsConfig = buildLocationMapsConfig(config.maps, showExactLocation);

  // Static image first, then upgrade to the interactive map once Mapbox GL has
  // loaded so the +/- zoom controls become usable.
  const useStaticMap = !isMapboxReady;

  const mapProps = {
    address,
    center: geolocation,
    obfuscatedCenter: geolocation, // Required when fuzzy.enabled
    mapsConfig: mapMapsConfig,
    useStaticMap,
    showFuzzyCircleInitially: !showExactLocation,
    ...(showExactLocation ? { zoom: EXACT_LOCATION_ZOOM } : {}),
  };

  return (
    <div className={css.root}>
      <h3 className={css.title}>
        <FormattedMessage
          id="StoreProfilePage.Sidebar.storeLocation"
          defaultMessage="Store Location"
        />
      </h3>
      <div className={css.mapContainer}>
        <Map
          key={`store-map-${geolocation.lat}-${geolocation.lng}-${useStaticMap ? 'static' : 'dynamic'}-${showExactLocation ? 'exact' : 'fuzzy'}`}
          {...mapProps}
        />
      </div>
      {/* The address itself is shown once, inline in the pickup-confirmed message
          below the map - not repeated up here. */}
      {showExactLocation ? (
        <div className={`${css.locationMessage} ${css.locationMessagePickup}`}>
          <FormattedMessage
            id="StoreLocationMap.pickupConfirmedMessage"
            values={{
              addressLine: (
                <span className={css.pickupAddressLine}>
                  <i className="ri-map-pin-2-line" aria-hidden="true" />
                  <strong>{address}</strong>
                </span>
              ),
            }}
          />
        </div>
      ) : (
        <div className={css.locationMessage}>
          <IconCollection className={css.infoIcon} name="info" />
          <FormattedMessage id="DeliveryPickupOptionsField.exactLocationAfterBooking" />
        </div>
      )}
    </div>
  );
};

StoreLocationMap.propTypes = {
  geolocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  address: PropTypes.string,

  // Store owner profile flag (publicData.showAddressInStore). When true and an
  // address exists, the map shows a precise pin plus the address text instead of
  // the privacy radius circle. Defaults to false.
  showAddressInStore: PropTypes.bool,
};

export default StoreLocationMap;
